<script>
import { mapActions } from 'vuex';
import { ref, onMounted } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import cookies from 'vue-cookies';
import MAX_SCREEN_WIDTH from '@/utils/maxScreenWidth';
import useResizeEvent from '@/utils/resizeEvent';
import priceFormat from '@/utils/priceFormat';
import SwiperCore, { Pagination } from 'swiper';
import Badge from '../Common/Badge';
import SvgFuel from '@/components/Svg/Fuel';
import VButton from '@/components/Common/VButton';
import SvgTransmission from '@/components/Svg/Transmission';
import SvgDrive from '@/components/Svg/Drive';
import GuaranteeInverted from '@/components/Svg/GuaranteeInverted';
import { MAX_MILEAGE_TO_SHOW_BADGE } from './constants';
import CarFavorite from '@/components/CarFavorite';

SwiperCore.use([Pagination]);

const SWIPER_OPTIONS = {
  slidesPerView: 1,
  threshold: 20,
};

const CURRENT_ELEMENTS = 8;

export default {
  name: 'CarItem',
  props: {
    car: {
      type: Object,
      default: () => ({}),
    },
    credit: {
      type: String,
    },
    cardType: {
      type: String,
      default: 'primary',
    },
    typePage: {
      type: String,
    },
  },
  components: {
    Swiper,
    SwiperSlide,
    VButton,
    SvgFuel,
    SvgTransmission,
    SvgDrive,
    Badge,
    GuaranteeInverted,
    CarFavorite,
  },
  MAX_SCREEN_WIDTH,
  SWIPER_OPTIONS,
  CURRENT_ELEMENTS,
  cookies,
  setup() {
    const screenWidth = ref(0);
    onMounted(() => {
      const { windowWidth } = useResizeEvent();
      screenWidth.value = { windowWidth }.windowWidth;
    });
    return { screenWidth };
  },

  data() {
    return {
      swiper: {},
      hoveredItem: false,
      credit: this.credit,
    };
  },
  computed: {
    shouldShowMileageBadge() {
      return this.car.mileage < MAX_MILEAGE_TO_SHOW_BADGE;
    },
    shouldShowBadges() {
      return this.shouldShowMileageBadge;
    },
    carMileage() {
      return `${priceFormat(this.car.mileage)} км`;
    },
    carWarranty() {
      return this.car.warranty;
    },
    isSold() {
      if (typeof this.car.images === 'string') {
        return this.car.images === '/public/images/svg/car.svg';
      }
      const isSold = this.car.images[0] === '/public/images/svg/car.svg';
      return isSold;
    },

    hasWarranty() {
      return (
        (this.typePage === 'used' && this.car.vehicle_state === 'excellent') ||
        (this.typePage === 'used' && this.car.vehicle_state === 'good')
      );
    },
  },
  watch: {
    credit(val) {
      this.credit = val;
    },
  },
  methods: {
    ...mapActions({
      togglePopup: 'Popups/togglePopup',
    }),
    BindClass: function () {
      return window.location.pathname.includes('/cars/used')
        ? 'bg-gray-400 hover:bg-gray-301 border-y'
        : 'bg-green hover:bg-blue-light';
    },
    onSwiper(swiper) {
      this.swiper = swiper;
    },
    swiperMouseEnter(keySlide) {
      this.swiper.slideTo(keySlide, 0);
      this.hoveredItem = true;
    },
    swiperMouseLeave() {
      this.swiper.slideTo(0, 0);
    },
    mouseOver() {
      if (this.screenWidth.value <= this.$options.MAX_SCREEN_WIDTH.laptop) {
        this.hoveredItem = true;
      }
    },
    handleCarCard(e, car) {
      if (this.cardType === 'secondary' || this.isSold) {
        e.preventDefault();
      }
    },
    openPopup() {
      this.togglePopup('order-call-minute');
    },
  },
};
</script>

<template :key="car.id">
  <a
    class="relative flex flex-col h-full bg-white rounded-big-brand p-4 transition duration-200 ease-in-out hover:shadow-brand"
    :href="this.isSold ? null : credit ? car.url + `?${credit}` : car.url"
    :class="{ 'h-fit': this.isSold, 'pointer-events-none': this.isSold }"
    @click="handleCarCard($event, car)"
  >
    <CarFavorite
      class="absolute top-[25px] right-[25px] flex justify-center items-center p-2.5 w-10 h-10 text-white rounded-brand bg-orange shadow-light cursor-pointer hover:text-white hover:bg-orange transition duration-200 ease-in-out z-10"
      :car-id="car.id"
      :car-type="typePage"
      v-if="!this.isSold"
    ></CarFavorite>
    <!-- <img
      v-if="car.hot_offer && typePage === 'stock'"
      src="/public/images/tire.png"
      alt=""
      class="z-10 custom -left-2.5 max-w-[50px]"
    />
    <img
      v-if="car.hot_offer && typePage === 'stock'"
      src="/public/images/tireGift.png"
      alt=""
      class="z-10 absolute max-w-[225px]"
    /> -->

    <!-- <img
      v-if="car.price_min && typePage === 'used'"
      src="/public/images/black-friday.png"
      alt=""
      class="z-10 absolute top-[15px] left-[-5px] max-w-[100px]"
    /> -->
    <img
      v-if="car.hot_offer"
      src="/public/images/fire-offer.png"
      alt=""
      class="z-10 absolute top-[-13px] -left-2.5 max-w-[50px]"
    />

    <img
      v-else-if="car.sale_status"
      src="/public/images/special-offer-2.png"
      alt=""
      class="z-10 absolute top-[-30px] left-0 max-w-[140px]"
    />

    <div
      class="sm:h-52 md:h-64 lg:h-52 xl:h-48 2xl:h-60 w-full rounded-big-brand overflow-hidden relative z-0"
    >
      <div
        v-if="
          car.images && typeof car.images === 'object' && car.images.length > 1
        "
        class="relative h-full"
      >
        <swiper
          :slides-per-view="$options.SWIPER_OPTIONS.slidesPerView"
          :threshold="$options.SWIPER_OPTIONS.threshold"
          pagination
          class="swiper swiper-car-card h-full"
          @swiper="onSwiper"
          @touchmove="mouseOver"
          @mouseenter="mouseOver"
        >
          <swiper-slide
            v-for="(image, key) in car.images.slice(
              0,
              $options.CURRENT_ELEMENTS,
            )"
            :key="key"
            class="swiper-slide relative"
          >
            <img
              :src="key === 0 || hoveredItem ? image : ''"
              alt=""
              class="w-full h-full object-cover"
            />
            <div
              v-if="
                key === $options.CURRENT_ELEMENTS - 1 &&
                car.images.length > $options.CURRENT_ELEMENTS
              "
              class="z-10 absolute top-0 left-0 w-full h-full flex items-center justify-center flex-col bg-black/50"
            >
              <div class="text-white font-bold text-3xl">
                +<span>{{
                  car.images.length - $options.CURRENT_ELEMENTS
                }}</span>
              </div>
              <div class="text-white font-medium text-2xl">&nbsp;фото</div>
            </div>
          </swiper-slide>
        </swiper>
        <div
          class="hidden lg:flex w-[calc(100%-28px)] h-full justify-center absolute bottom-0 z-10 mx-3.5"
        >
          <div
            v-for="(image, key) in car.images.slice(
              0,
              $options.CURRENT_ELEMENTS,
            )"
            :key="key"
            class="h-full"
            :style="`width: ${car.images.length >= $options.CURRENT_ELEMENTS ? '12.5%' : 100 / car.images.length + '%'}`"
            @mouseenter="swiperMouseEnter(key)"
            @mouseleave="swiperMouseLeave"
          ></div>
        </div>
      </div>
      <img
        v-else-if="
          car.images &&
          typeof car.images === 'object' &&
          car.images.length === 1
        "
        :src="car.images[0]"
        alt=""
        class="w-full h-full object-cover"
      />
      <img
        v-else-if="typeof this.car.images === 'string'"
        :src="car.images"
        alt=""
        class="w-full h-full object-cover"
      />
      <div
        v-if="shouldShowBadges"
        class="absolute top-[12px] right-[60px] flex gap-2"
      >
        <badge v-if="shouldShowMileageBadge" :text="carMileage" />
      </div>
      <div
        v-if="car.is_booked === 1"
        class="absolute top-0 left-0 h-full w-full overflow-hidden"
      >
        <div
          class="absolute top-0 left-0 h-full w-full opacity-60 bg-white"
        ></div>
        <p
          class="absolute uppercase w-[150%] -rotate-[36deg] sm:-rotate-[37deg] lg:-rotate-[33deg] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center bg-white bg-opacity-80 p-2"
        >
          Извините, меня уже забронировали
        </p>
      </div>
    </div>
    <div class="mt-2.5 md:mt-4 lg:mt-5 mb-3">
      <div
        class="flex flex-col-reverse md:flex-row justify-between items-start md:items-center"
      >
        <div
          class="font-fira text-gray-800 font-medium text-xs lg:text-xl size-xxs mt-2.5 md:mt-0"
        >
          {{ car.name }}
        </div>
        <div class="flex gap-[5px] relative z-10">
          <div
            v-if="this.hasWarranty"
            class="absolute bottom-[110%] z-10"
            title="На этот автомобиль есть гарантия. Все подробности уточняйте в отделе продаж."
          >
            <img src="/public/images/warranty.png" alt="warranty" />
          </div>
          <div
            v-if="cardType === 'primary'"
            class="font-roboto w-fit text-gray-500 text-xxs md:text-xxs font-medium py-1 px-1 md:px-2.5 border border-gray-500 rounded"
          >
            {{ car.year }}
          </div>
        </div>
      </div>
    </div>
    <p
      v-if="car.warranty"
      class="text-green rounded-full bg-green-400 bg-opacity-10 px-[10px] py-[8px] mb-3 flex flex-start gap-2 self-start items-center"
    >
      <i class="w-5 h-5 shrink-0">
        <guarantee-inverted />
      </i>
      {{ car.warranty }}
    </p>

    <div class="mt-auto pb-4" :class="{ 'mt-[0]': this.isSold }">
      <div class="flex justify-between">
        <div>
          <div
            v-if="!car.price_min"
            class="font-roboto whitespace-nowrap text-green font-medium text-base md:text-xl 2xl:text-2xl"
          >
            {{ car.price }}
            <small
              class="font-roboto text-green font-medium text-xs md:text-sm lg:text-base"
            >
              &#8381;
            </small>
          </div>
          <div v-else>
            <div
              class="uppercase whitespace-nowrap text-gray-500 text-xxs lg:text-xs line-through"
            >
              {{ car.price }}&nbsp;&#8381;
            </div>
            <div
              class="font-roboto whitespace-nowrap text-green font-medium text-base md:text-xl 2xl:text-2xl"
            >
              {{ car.price_min }}
              <small
                class="font-roboto text-green font-medium text-xs md:text-sm lg:text-base"
              >
                &#8381;
              </small>
            </div>
          </div>
        </div>
        <template v-if="car.credit_price && cardType === 'primary'">
          <div class="border-l border-gray-300"></div>
          <div class="flex justify-end items-center h-7 self-end">
            <p
              class="whitespace-nowrap text-s text-white leading-none px-[10px] py-[4px] bg-orange rounded-3xl"
            >
              от {{ car.credit_price }} &#8381;/мес
            </p>
          </div>
        </template>
      </div>
      <hr class="h-px bg-gray-300 w-full mt-2.5 md:mt-3 lg:mt-4" />
      <div class="flex justify-between mt-2.5 md:mt-3 lg:mt-4 text-xxs">
        <div class="flex items-center gap-x-1 md:gap-x-1.5 2xl:gap-x-2.5">
          <svg-fuel class="w-3.5 md:w-4 h-auto"></svg-fuel>
          <div class="leading-none">{{ car.fuel }}</div>
        </div>

        <div class="flex items-center gap-x-1 md:gap-x-1.5 2xl:gap-x-2.5">
          <svg-transmission class="w-3.5 md:w-4 h-auto"></svg-transmission>
          <div class="leading-none">{{ car.kpp }}</div>
        </div>
        <div class="flex items-center gap-x-1 md:gap-x-1.5 2xl:gap-x-2.5">
          <svg-drive class="w-3.5 md:w-4 h-auto"></svg-drive>
          <div class="leading-none">{{ car.gear }}</div>
        </div>
      </div>
    </div>

    <div
      v-if="this.cardType === 'primary' && !this.isSold"
      class="bg-green rounded-md flex items-center justify-center text-white text-semibold text-sm lg:text-base h-10 lg:h-12 hover:bg-blue-light hover:text-gray-600 cursor-pointer transition ease-in-out duration-200"
    >
      Подробнее
    </div>

    <v-button
      v-if="this.cardType === 'secondary' && !this.isSold"
      popup-name="order-call"
      popup-title="Авто на заказ"
      :car-name="car.name"
      action="cars-order"
      class="bg-green rounded-md flex items-center justify-center text-white text-semibold text-sm lg:text-base h-10 lg:h-12 hover:bg-blue-light hover:text-gray-600 cursor-pointer transition ease-in-out duration-200"
      @click.stop.prevent
    >
      Заказать
    </v-button>
    <div
      v-if="!this.isSold"
      :class="BindClass()"
      @click.stop.prevent="openPopup"
      class="z-2 rounded-md flex items-center justify-center text-white text-semibold text-sm lg:text-base h-10 lg:h-12 cursor-pointer mt-[10px] transition ease-in-out duration-200"
    >
      Перезвоним за минуту
    </div>
  </a>
</template>

<style scoped>
.custom {
  position: absolute;
  top: 13.5rem;
  @media (max-width: 1540px) {
    top: 10.5rem;
  }
  @media (max-width: 1275px) {
    top: 11.5rem;
  }
  @media (max-width: 1024px) {
    top: 14.5rem;
  }
  @media (max-width: 376px) {
    top: 25rem;
  }
}

/* .size-xxs {
  @media (min-width: 1280px) and (max-width: 1400px) {
    font-size: 16px;
  }
} */
</style>
